import { useState } from 'react';
import Button from '../ui/Button';
import { Dialog } from './accounts/AccountBits';
import nurseryService from '../../services/nursery';
import { errorMessage } from '../../utils/apiResponse';
import { cn } from '../../utils/cn';
import { getTodayInTz, useOrgTimezone } from '../../utils/timezone';
import { LOT_STAGE_LABELS, MANUAL_LOT_STAGES } from '../../types/nursery';
import type { LotChangeResult, LotStage, NurseryLot } from '../../types/nursery';

export type LotChangeMode = 'stage' | 'archive' | 'restore';

type LotLike = Pick<NurseryLot, 'id' | 'cultivar_name' | 'rootstock_name' | 'current_stage' | 'status' | 'current_count'>
  & { lot_code?: string | null };

function stageLabel(stage: string): string {
  return LOT_STAGE_LABELS[stage as LotStage] ?? stage;
}

function lotName(l: LotLike): string {
  return `${l.cultivar_name ?? 'Lot'}${l.rootstock_name ? ` / ${l.rootstock_name}` : ''}${l.lot_code ? ` (${l.lot_code})` : ` #${l.id}`}`;
}

/**
 * Change the stage of one lot or many, or take them out of stock and back.
 *
 * Free stock counts only ready lots, so "Ready to sell" is what puts trees on
 * the order sheet. Every change is recorded on the lot's history. The server
 * changes the lots it can and says why it left the rest; this shows that,
 * rather than closing as though everything moved.
 */
export function LotStageDialog({ lots, mode, initialStage, onClose, onDone }: {
  lots: LotLike[];
  mode: LotChangeMode;
  initialStage?: LotStage;
  onClose: () => void;
  /** Called once something changed, before the dialog closes. */
  onDone: (result: LotChangeResult) => void;
}) {
  const tz = useOrgTimezone();
  const stages = new Set(lots.map(l => l.current_stage));
  const sharedStage = stages.size === 1 ? lots[0]?.current_stage : null;

  const [stage, setStage] = useState<LotStage | ''>(initialStage ?? '');
  const [date, setDate] = useState(getTodayInTz(tz));
  const [readyDate, setReadyDate] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LotChangeResult | null>(null);

  const trees = lots.reduce((n, l) => n + (l.current_count || 0), 0);
  const count = `${lots.length} lot${lots.length === 1 ? '' : 's'}`;
  const subtitle = lots.length === 1
    ? `${lotName(lots[0])} · ${trees.toLocaleString()} trees`
    : `${count} · ${trees.toLocaleString()} trees`;
  const leavingReady = stage !== '' && stage !== 'ready' && lots.some(l => l.current_stage === 'ready');

  const title = mode === 'archive' ? 'Take out of stock'
    : mode === 'restore' ? 'Put back in stock'
    : 'Change stage';

  const submit = async () => {
    if (mode === 'stage' && !stage) { setError('Choose a stage'); return; }
    setBusy(true);
    setError(null);
    try {
      const ids = lots.map(l => l.id);
      const res = mode === 'stage'
        ? await nurseryService.changeLotStage({
          lot_ids: ids, stage: stage as LotStage, date,
          ready_date: stage !== 'ready' && readyDate ? readyDate : undefined,
          notes: notes || null,
        })
        : await nurseryService.changeLotStatus({ lot_ids: ids, action: mode, date, notes: notes || null });
      if (!res.success) { setError(errorMessage(res, 'Nothing was changed')); setBusy(false); return; }
      if (res.data.changed.length > 0) onDone(res.data);
      if (res.data.skipped.length === 0) { onClose(); return; }
      setResult(res.data);
    } catch (e) {
      setError(errorMessage(e, 'Nothing was changed'));
    }
    setBusy(false);
  };

  if (result) {
    const byId = new Map(lots.map(l => [l.id, l]));
    return (
      <Dialog title={title} subtitle={subtitle} onClose={onClose}
        footer={<Button onClick={onClose}>Done</Button>}>
        {result.changed.length > 0 && (
          <p className="text-sm text-green-700">
            {result.changed.length} of {lots.length} lot{lots.length === 1 ? '' : 's'} changed.
          </p>
        )}
        <p className="text-sm text-neutral-700">
          {result.skipped.length === lots.length ? 'Nothing was changed:' : 'Left as they were:'}
        </p>
        <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded-lg">
          {result.skipped.map(s => (
            <li key={s.id} className="px-3 py-2 text-sm">
              <div className="font-medium text-neutral-800 break-words">{byId.get(s.id) ? lotName(byId.get(s.id)!) : `Lot #${s.id}`}</div>
              <div className="text-neutral-500 break-words">{s.reason}</div>
            </li>
          ))}
        </ul>
      </Dialog>
    );
  }

  return (
    <Dialog
      title={title}
      subtitle={subtitle}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || (mode === 'stage' && !stage)}
            variant={mode === 'archive' ? 'danger' : 'primary'}>
            {busy ? 'Saving…'
              : mode === 'archive' ? `Take ${count} out of stock`
              : mode === 'restore' ? `Put ${count} back`
              : stage ? `Move ${count} to ${stageLabel(stage).toLowerCase()}` : 'Choose a stage'}
          </Button>
        </>
      }
    >
      {mode === 'stage' && (
        <>
          <div>
            <div className="text-sm font-medium text-neutral-700 mb-2">Move to</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" role="radiogroup" aria-label="Stage">
              {MANUAL_LOT_STAGES.map(s => {
                const isNow = sharedStage === s;
                const on = stage === s;
                return (
                  <button key={s} type="button" role="radio" aria-checked={on} disabled={isNow}
                    onClick={() => setStage(s)}
                    className={cn(
                      'rounded-lg border px-2 py-2 text-sm text-left min-w-0',
                      s === 'ready' && 'col-span-2 sm:col-span-4 font-medium',
                      on ? (s === 'ready' ? 'border-green-600 bg-green-50 text-green-800 ring-1 ring-green-600'
                        : 'border-primary-600 bg-primary-50 text-primary-800 ring-1 ring-primary-600')
                        : 'border-neutral-300 text-neutral-700 hover:bg-neutral-50',
                      isNow && 'opacity-50 cursor-not-allowed',
                    )}>
                    <span className="break-words">{stageLabel(s)}</span>
                    {isNow && <span className="block text-xs text-neutral-500">now</span>}
                  </button>
                );
              })}
            </div>
            {!sharedStage && lots.length > 1 && (
              <p className="text-xs text-neutral-500 mt-1">These lots are at different stages now: {[...stages].map(stageLabel).join(', ')}.</p>
            )}
          </div>

          {stage === 'ready' && (
            <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2 break-words">
              These trees count as free stock from now: they show on the order sheet and orders can be allocated from them.
            </p>
          )}

          {stage !== '' && stage !== 'ready' && (
            <div>
              <label htmlFor="lot-stage-ready" className="block text-sm font-medium text-neutral-700 mb-1">Expected ready date (optional)</label>
              <input id="lot-stage-ready" type="date" value={readyDate} onChange={e => setReadyDate(e.target.value)}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
              <p className="text-xs text-neutral-500 mt-1 break-words">
                {leavingReady
                  ? 'Leave blank to work it out again from the tree type. Lots with trees allocated to orders stay ready until those are released.'
                  : 'Leave blank to keep the date each lot has.'}
              </p>
            </div>
          )}
        </>
      )}

      {mode === 'archive' && (
        <p className="text-sm text-neutral-700 break-words">
          {trees.toLocaleString()} trees stop counting as stock. The lots and their history stay on record and can be put
          back. A lot with trees allocated to orders is left as it is.
        </p>
      )}
      {mode === 'restore' && (
        <p className="text-sm text-neutral-700 break-words">
          The lots count as stock again, at the stage and place they were archived at.
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="lot-stage-date" className="block text-sm font-medium text-neutral-700 mb-1">Date</label>
          <input id="lot-stage-date" type="date" value={date} onChange={e => setDate(e.target.value)}
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label htmlFor="lot-stage-notes" className="block text-sm font-medium text-neutral-700 mb-1">
            {mode === 'archive' ? 'Reason' : 'Notes'}
          </label>
          <input id="lot-stage-notes" value={notes} onChange={e => setNotes(e.target.value)}
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words">{error}</div>}
    </Dialog>
  );
}

export default LotStageDialog;
